/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { loadGLB } from '../assets/loaders.ts'
import { frame } from '../flight/state.ts'
import { DESKS } from '../world.ts'
import { TYPING } from '../shaders/chunks.ts'
import { glsl, SURFACE3 } from '../palette.ts'

// The hero corner: a real 3D ape (owner-supplied Meshy sculpt, simplified from
// 1.96M to ~30k triangles — dev_guides/tools/meshy), a small dark writing desk,
// and a Meshy-generated typewriter (textures stripped, ~4k triangles). All three
// share one ink shader — geometry only, shaded in the site's palette, because the
// monkeys are near-black animals by law, not textured renders. This replaces the
// old atlas-cell hero quad, whose composition was clipped at the cell edges.

const VERT = /* glsl */ `
uniform float uTime, uFog, uBob;
uniform vec3 uBg, uDark, uLit;
varying vec3 vCol;
${TYPING}
void main() {
  vec3 p = position;
  // breathing (whole body, barely) + typing strokes on the hands (uBob=0 for furniture)
  p.y += sin(uTime * 1.15) * uBob * smoothstep(0.2, 1.4, position.y);
  if (uBob > 0.0) p = typing(p, uTime, 3.7);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  vec3 n = normalize(normalMatrix * normal);
  vec3 v = normalize(-mv.xyz);
  float l = 0.5 + 0.5 * dot(n, normalize(vec3(0.3, 1.0, 0.45)));
  float fill = max(dot(n, v), 0.0) * 0.4;
  vec3 col = mix(uDark, uLit, clamp(l * 0.85 + fill, 0.0, 1.0));
  float rim = pow(1.0 - abs(dot(n, v)), 2.2);
  col = mix(col, ${glsl(SURFACE3)}, rim * 0.45);
  float dist = length(mv.xyz);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  vCol = mix(col, uBg, fo);
}
`
const FRAG = /* glsl */ `
varying vec3 vCol;
void main() { gl_FragColor = vec4(vCol, 1.0); }
`

function inkMaterial(dark: string, lit: string, bob: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uFog: { value: 0.016 },
      uBob: { value: bob },
      uBg: { value: new THREE.Color() },
      uDark: { value: new THREE.Color(dark) },
      uLit: { value: new THREE.Color(lit) },
    },
  })
}

// the hero faces along its model's local +z after this yaw
const hero = DESKS[0]
const YAW = hero.yaw - Math.PI + 1.05
const FX = Math.sin(YAW)
const FZ = Math.cos(YAW)
// the writing desk sits in front of the hands
const DESK = { x: hero.x + FX * 0.72, z: hero.z + FZ * 0.72, top: 0.5 }

export function HeroApe() {
  const [apeGeo, setApeGeo] = useState<THREE.BufferGeometry | null>(null)
  const [twGeo, setTwGeo] = useState<THREE.BufferGeometry | null>(null)
  const [mats] = useState(() => ({
    ape: inkMaterial('#1B2126', '#4E5860', 0.006),
    furniture: inkMaterial('#14181C', '#333B42', 0),
  }))
  const [deskGeo] = useState(() => {
    const parts: THREE.BufferGeometry[] = []
    const slab = new THREE.BoxGeometry(0.72, 0.045, 0.46)
    slab.translate(0, DESK.top - 0.0225, 0)
    parts.push(slab)
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const leg = new THREE.BoxGeometry(0.04, DESK.top - 0.045, 0.04)
        leg.translate(sx * 0.32, (DESK.top - 0.045) / 2, sz * 0.19)
        parts.push(leg)
      }
    }
    return mergeGeometries(parts)!
  })

  useEffect(() => {
    const a = loadGLB('/assets/hero-ape.glb', (g) => {
      g.computeVertexNormals()
      g.computeBoundingBox()
      g.translate(0, -g.boundingBox!.min.y, 0) // ground the feet
      setApeGeo(g)
    })
    const b = loadGLB('/assets/typewriter.glb', (g) => {
      g.computeVertexNormals()
      g.computeBoundingBox()
      const bb = g.boundingBox!
      const s = 0.5 / (bb.max.x - bb.min.x) // 50 cm wide on the desk
      g.scale(s, s, s)
      g.computeBoundingBox()
      g.translate(
        -(g.boundingBox!.min.x + g.boundingBox!.max.x) / 2,
        -g.boundingBox!.min.y,
        -(g.boundingBox!.min.z + g.boundingBox!.max.z) / 2,
      )
      setTwGeo(g)
    })
    return () => {
      a()
      b()
    }
  }, [])

  useFrame((st) => {
    for (const m of [mats.ape, mats.furniture]) {
      m.uniforms.uTime.value = st.clock.elapsedTime
      m.uniforms.uFog.value = frame.beats.fog
      ;(m.uniforms.uBg.value as THREE.Color).copy(frame.bg)
    }
  })

  return (
    <>
      {apeGeo && (
        <mesh
          geometry={apeGeo}
          material={mats.ape}
          position={[hero.x, 0, hero.z]}
          rotation-y={YAW}
          scale={0.88}
        />
      )}
      <mesh
        geometry={deskGeo}
        material={mats.furniture}
        position={[DESK.x, 0, DESK.z]}
        rotation-y={YAW}
      />
      {twGeo && (
        <mesh
          geometry={twGeo}
          material={mats.furniture}
          position={[DESK.x, DESK.top, DESK.z]}
          rotation-y={YAW + Math.PI}
        />
      )}
    </>
  )
}
